import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import './JobApplicationModal.scss';

import { updateJob, createJob, deleteJob } from '../../../Actions/FeaturedActions';
import { submitJobApplication } from '../../../APIClient/jobs';
import { isAdminControlMode } from '../../../Helpers/ui_helpers';

import Modal from '../../General/Modal';
import Switch from 'react-switch';
import Select from 'react-select';
import Loader from '../../Loader/Loader';
import UploadImage from '../../General/UploadImage';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faFileUpload } from '@fortawesome/pro-light-svg-icons';

const departments = [
  'Brand Growth',
  'Brand Success',
  'Creator Growth',
  'Creator Success',
  'Product',
  'Engineering',
  'Design',
  'Marketing',
  'HR',
  'Finance'
];

const JobApplicationModal = props => {
  const { close, ui, job } = props;
  const inAdminControlMode = isAdminControlMode(ui);

  const [isSaving, setIsSaving] = React.useState(false);

  // state for user applying to job
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [linkedin, setLinkedin] = React.useState('');
  const [website, setWebsite] = React.useState('');
  const [extraNotes, setExtraNotes] = React.useState('');
  const [resume, setResume] = React.useState(null);
  const [resumeUplaoding, setResumeUploading] = React.useState(false);
  const [coverLetter, setCoverLetter] = React.useState(null);
  const [coverLetterUploading, setCoverLetterUploading] = React.useState(false);

  // state for admin editing job
  const isExistingJob = !!job?.id;
  const [stillAvailable, setStillAvailable] = React.useState(true);
  const [title, setTitle] = React.useState(job?.title || '');
  const [department, setDepartment] = React.useState(job?.department || '');
  const [summary, setSummary] = React.useState(job?.summary || '');
  const [qualifications, setQualifications] = React.useState(job?.qualifications || '');
  const [responsibilities, setResponsibilities] = React.useState(job?.responsibilities || '');

  const createJob = () => {
    if (isSaving) return;

    setIsSaving(true);
    const data = { title, department, summary, qualifications, responsibilities, stillAvailable };
    props.createJob(data).then(() => {
      setIsSaving(false);
      close();
    });
  };

  const updateJob = () => {
    if (isSaving) return;

    setIsSaving(true);
    const data = { title, department, summary, qualifications, responsibilities, stillAvailable };
    props.updateJob(job.id, data).then(() => {
      setIsSaving(false);
      close();
    });
  };

  const deleteJob = () => {
    if (isSaving) return;
    else if (!window.confirm('Are you sure you want to delete this job?')) return;

    setIsSaving(true);
    props.deleteJob(job.id).then(() => {
      setIsSaving(false);
      close();
    });
  };

  const submitApplication = () => {
    const missingFields = [];
    if (!name) missingFields.push('Name');
    if (!email) missingFields.push('Email');
    if (!resume) missingFields.push('Resume');

    if (missingFields.length) {
      window.ALERT.error(`Please fill out the following fields: ${missingFields.join(', ')}`);
      return;
    }

    setIsSaving(true);
    const data = { position: job.title, department: job.department, name, email, resume, coverLetter, linkedin, website, extraNotes };
    submitJobApplication(data)
      .then(() => {
        window.ALERT.success('Application submitted! Please check your email for confirmation.');
        close();
      })
      .catch(() => window.ALERT.error('Error submitting application. Please try again later.'))
      .finally(() => setIsSaving(false));
  };

  return (
    <Modal visible showClose close={close} className='job-application-modal-outer' smallForm>
      <div className='job-application-modal-inner'>
        {inAdminControlMode ? (
          <>
            {/*****************************************************************************/}
            {/* ADMIN PORTAL */}
            {/*****************************************************************************/}
            <div className='apply-modal-header'>
              <div className='apply-modal-title'>Edit Job</div>
            </div>
            <div className='apply-modal-body'>
              <div className='input-container'>
                <div className='input-label'>Display Job</div>
                <Switch
                  onChange={() => setStillAvailable(!stillAvailable)}
                  checked={stillAvailable}
                  height={14}
                  width={24}
                  onColor='#333'
                  offColor='#ccc'
                  className='switch'
                  checkedIcon={false}
                  uncheckedIcon={false}
                />
              </div>
              <div className='input-container'>
                <div className='input-label'>Title</div>
                <input className='modal-input' type='text' value={title} onChange={e => setTitle(e.target.value)} placeholder='Name' />
              </div>
              <div className='input-container'>
                <div className='input-label'>Department</div>
                <Select
                  options={departments.map(department => ({ label: department, value: department }))}
                  value={{ label: department, value: department }}
                  onChange={e => setDepartment(e.value)}
                  className='select-input'
                />
              </div>
              <div className='input-container'>
                <div className='input-label'>Summary</div>
                <textarea rows='15' value={summary || ''} onChange={e => setSummary(e.target.value)}></textarea>
              </div>
              <div className='input-container'>
                <div className='input-label'>
                  Qualifications <span>(double enter for new)</span>
                </div>
                <textarea rows='15' value={qualifications || ''} onChange={e => setQualifications(e.target.value)}></textarea>
              </div>
              <div className='input-container'>
                <div className='input-label'>
                  Responsibilities <span>(double enter for new)</span>
                </div>
                <textarea rows='15' value={responsibilities || ''} onChange={e => setResponsibilities(e.target.value)}></textarea>
              </div>
            </div>
            <div className='apply-modal-button-container'>
              {isExistingJob && (
                <button className='delete-button' onClick={deleteJob}>
                  Delete Listing
                </button>
              )}
              <button className='submit-button' onClick={isExistingJob ? updateJob : createJob}>
                {isSaving ? <Loader size={25} /> : 'Save Job Listing'}
              </button>
            </div>
          </>
        ) : (
          <>
            {/*****************************************************************************/}
            {/* USER PORTAL */}
            {/*****************************************************************************/}
            <div className='apply-modal-header'>
              <div className='apply-modal-subtitle'>Application for</div>
              <div className='apply-modal-title'>{title}</div>
            </div>
            <div className='apply-modal-body'>
              <div className='input-container'>
                <div className='input-label'>Name</div>
                <input className='modal-input' type='text' value={name} onChange={e => setName(e.target.value)} placeholder='Name' />
              </div>
              <div className='input-container'>
                <div className='input-label'>Email</div>
                <input className='modal-input' type='text' value={email} onChange={e => setEmail(e.target.value)} placeholder='Email' />
              </div>
              <div className='input-container'>
                <div className='input-label'>
                  Resume <span>(only .pdf)</span>
                </div>
                <UploadImage
                  formalOnly
                  basicUploader
                  basicTextExplainer={'Upload Resume'}
                  basicContent={
                    resume ? (
                      <div className='upload-pdf-container'>
                        <div>
                          Resume Upload Complete
                          <FontAwesomeIcon icon={faCheck} />
                        </div>
                      </div>
                    ) : (
                      <div className='upload-pdf-container'>
                        {resumeUplaoding ? (
                          <span>Uploading...</span>
                        ) : (
                          <div>
                            Upload Resume
                            <FontAwesomeIcon icon={faFileUpload} />
                          </div>
                        )}
                      </div>
                    )
                  }
                  onUploadProgress={() => {
                    setResumeUploading(true);
                  }}
                  completeUpload={resumeUrl => {
                    setResume(resumeUrl);
                    setResumeUploading(false);
                  }}
                />
              </div>
              <div className='input-container'>
                <div className='input-label'>
                  Cover Letter <span>(only .pdf)</span>
                </div>
                <UploadImage
                  formalOnly
                  basicUploader
                  basicTextExplainer={'Upload Cover Letter'}
                  basicContent={
                    coverLetter ? (
                      <div className='upload-pdf-container'>
                        <div>
                          Cover Letter Upload Complete
                          <FontAwesomeIcon icon={faCheck} />
                        </div>
                      </div>
                    ) : (
                      <div className='upload-pdf-container'>
                        {coverLetterUploading ? (
                          <span>Uploading...</span>
                        ) : (
                          <div>
                            Upload Cover Letter
                            <FontAwesomeIcon icon={faFileUpload} />
                          </div>
                        )}
                      </div>
                    )
                  }
                  onUploadProgress={() => {
                    setCoverLetterUploading(true);
                  }}
                  completeUpload={resumeUrl => {
                    setCoverLetter(resumeUrl);
                    setCoverLetterUploading(false);
                  }}
                />
              </div>
              <div className='input-container'>
                <div className='input-label'>LinkedIn</div>
                <input
                  className='modal-input'
                  type='url'
                  value={linkedin}
                  onChange={e => setLinkedin(e.target.value)}
                  placeholder='https://www.linkedin.com/in/.... (optional)'
                />
              </div>
              <div className='input-container'>
                <div className='input-label'>Website</div>
                <input
                  className='modal-input'
                  type='url'
                  value={website}
                  onChange={e => setWebsite(e.target.value)}
                  placeholder='https://your-site.com/ (optional)'
                />
              </div>
              <div className='input-container'>
                <div className='input-label'>Extra Notes</div>
                <textarea
                  rows='15'
                  value={extraNotes || ''}
                  onChange={e => setExtraNotes(e.target.value)}
                  placeholder='Add anything extra on why you would be an awesome fit for the role! (optional)'
                ></textarea>
              </div>
            </div>
            <div className='apply-modal-button-container'>
              <button className='submit-button' onClick={submitApplication} disabled={resumeUplaoding || coverLetterUploading || isSaving}>
                {resumeUplaoding || coverLetterUploading || isSaving ? <Loader size={25} /> : 'Submit Application'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const mapStateToProps = state => {
  const { ui } = state;
  return { ui };
};

JobApplicationModal.propTypes = {
  close: PropTypes.func.isRequired,
  ui: PropTypes.object.isRequired,
  job: PropTypes.object.isRequired,

  updateJob: PropTypes.func.isRequired,
  createJob: PropTypes.func.isRequired
};

export default connect(mapStateToProps, {
  updateJob,
  createJob,
  deleteJob
})(JobApplicationModal);
